<template>
  <div class="vb-root">

    <!-- header rail. same ops rail language as ops_board: eyebrow + title
         on the left, base filter + manual refresh on the right. -->
    <div class="vb-rail">
      <div class="vb-rail-text">
        <div class="vb-rail-eyebrow">
          Ops &middot; VSM Preview
        </div>
        <h3 class="vb-rail-title">
          Vehicle Status Manager
          <span v-if="contextTenantName" class="vb-rail-tenant-chip">{{ contextTenantName }}</span>
        </h3>
      </div>
      <div class="vb-rail-actions-col">
      <div class="vb-rail-actions">
        <!-- base option list is shared with the legacy page; only the first
             label is re-cased client-side (see baseOptions) -->
        <b-form-select
          v-if="baseOptions.length > 2"
          v-model="selected_base"
          :options="baseOptions"
          size="sm"
          class="vb-base-select"
          @change="load_data()"
        />
        <!-- auto-refresh toggle: a live dot when on. the board is a wall
             surface, so this defaults on at the legacy standalone cadence -->
        <b-button
          size="sm"
          class="vb-auto-btn"
          :class="{ 'vb-auto-btn--on': auto_refresh }"
          variant="outline-secondary"
          :title="autoRefreshTitle"
          :aria-pressed="auto_refresh ? 'true' : 'false'"
          aria-label="Toggle auto-refresh"
          @click="toggleAutoRefresh"
        >
          <span class="vb-auto-dot" :class="{ 'vb-auto-dot--live': auto_refresh }" aria-hidden="true"></span>
          Auto
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="refreshing"
          title="Refresh now"
          aria-label="Refresh now"
          @click="load_data()"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': refreshing }"></i>
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          title="Print this board"
          aria-label="Print this board"
          @click="printPage"
        >
          <i class="fa fa-print"></i>
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          title="About this board"
          aria-label="About this board"
          @click="show_info = true"
        >
          <i class="fa fa-info-circle"></i>
        </b-button>
      </div>
      <div v-if="updatedAt" class="vb-rail-updated">updated {{ updatedAt }}</div>
      </div>
    </div>

    <!-- print-only header so a printed board is self-documenting (the rail
         actions and filters are hidden on paper) -->
    <div class="vb-print-meta" aria-hidden="true">
      <span class="vb-print-meta-title">Vehicle Status Manager<span v-if="contextTenantName"> &middot; {{ contextTenantName }}</span></span>
      <span v-if="summary" class="vb-print-meta-sub">{{ filteredItems.length }} of {{ summary.total_cnt }} vehicles<span v-if="summary.timezone"> &middot; {{ summary.timezone }}</span></span>
      <span class="vb-print-meta-when">Printed {{ printedStamp }}</span>
    </div>

    <!-- load failures surface inline so data already on screen stays put -->
    <div v-if="error" class="vb-error">
      <span>{{ error }}</span>
      <b-button size="sm" variant="outline-secondary" @click="load_data()">Retry</b-button>
    </div>

    <!-- the api sources the roster from the telematics feed, so a fleet
         with no connected provider comes back 404/empty. state that plainly
         instead of firing the retryable error banner. -->
    <div v-if="noData" class="vb-no-telematics">
      <i class="fa fa-plug" aria-hidden="true"></i>
      <div class="vb-no-telematics-title">No vehicle data for this fleet</div>
      <div class="vb-no-telematics-sub">Nothing is reporting here yet. If this tenant has no telematics integration, switch to one with a connected fleet (Samsara, Zonar, and the like) to preview vehicle status.</div>
    </div>

    <template v-else>
    <!-- filter chips: a standalone available chip (wash and maint both
         ready) leads, then two multi-select groups. available ORs with
         the wash group and the two groups AND together. counts always
         read from the unfiltered set so they stay stable while
         filtering. -->
    <div class="vb-chip-filters">
      <!-- on mobile this scroller is one swipeable pill row under the sticky
           search; on desktop it wraps inline to the left of the search -->
      <div class="vb-chip-scroll">
      <button
        type="button"
        :class="['vb-chip', 'vb-fc-green', { 'vb-chip--active': wash_filters.includes('available') }]"
        :aria-pressed="wash_filters.includes('available') ? 'true' : 'false'"
        @click="toggleWashFilter('available')"
      >Available ({{ countAvailable }})</button>
      <div class="vb-chip-group" role="group" aria-label="Wash status filters">
        <span class="vb-chip-group-label">Wash</span>
        <button
          v-for="chip in washChips"
          :key="'w-' + chip.value"
          type="button"
          :class="['vb-chip', 'vb-fc-' + chip.color, { 'vb-chip--active': wash_filters.includes(chip.value) }]"
          :aria-pressed="wash_filters.includes(chip.value) ? 'true' : 'false'"
          @click="toggleWashFilter(chip.value)"
        >{{ chip.label }} ({{ chip.count }})</button>
      </div>
      <div class="vb-chip-group" role="group" aria-label="Maintenance status filters">
        <span class="vb-chip-group-label">Maint</span>
        <button
          v-for="chip in maintChips"
          :key="'m-' + chip.value"
          type="button"
          :class="['vb-chip', 'vb-fc-' + chip.color, { 'vb-chip--active': maint_filters.includes(chip.value) }]"
          :aria-pressed="maint_filters.includes(chip.value) ? 'true' : 'false'"
          @click="toggleMaintFilter(chip.value)"
        >{{ chip.label }} ({{ chip.count }})</button>
      </div>
      </div>
      <!-- tags are tenant-defined and open-ended, so a compact menu keeps the
           pill rail stable where the bounded wash/maint enums use inline chips.
           sits outside .vb-chip-scroll so its menu isn't clipped by the row's
           overflow; hidden entirely when the feed carries no tags. -->
      <b-dropdown
        v-if="tagChips.length"
        size="sm"
        variant="outline-secondary"
        class="vb-tag-dd"
        no-caret
        menu-class="vb-tag-menu"
      >
        <template slot="button-content">
          <i class="fa fa-tag" aria-hidden="true"></i>
          Tags<span v-if="tag_filters.length" class="vb-tag-count">{{ tag_filters.length }}</span>
        </template>
        <b-dropdown-form class="vb-tag-form">
          <label
            v-for="chip in tagChips"
            :key="'t-' + chip.value"
            class="vb-tag-opt"
          >
            <input
              type="checkbox"
              :checked="tag_filters.includes(chip.value)"
              @change="toggleTagFilter(chip.value)"
            />
            <span class="vb-tag-opt-label">{{ chip.label }}</span>
            <span class="vb-tag-opt-count">{{ chip.count }}</span>
          </label>
        </b-dropdown-form>
      </b-dropdown>
      <!-- free-text find across vehicle name + driver(s) + status/base/id,
           mirroring the legacy board's search -->
      <div class="vb-search">
        <i class="fa fa-search vb-search-icon" aria-hidden="true"></i>
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          class="vb-search-input"
          placeholder="Find vehicle, driver, or note"
          aria-label="Find vehicle, driver, or note"
        />
        <button v-if="search" type="button" class="vb-search-clear" aria-label="Clear search" @click="clearSearch"><i class="fa fa-times" aria-hidden="true"></i></button>
      </div>
    </div>

    <!-- result count sits with the list it describes: it tracks the active
         filters, and the timezone labels the times shown in the rows below -->
    <div v-if="summary" class="vb-results-meta">
      <span class="vb-results-count">{{ filteredItems.length }} of {{ summary.total_cnt }} vehicles</span>
      <span v-if="summary.timezone" class="vb-results-tz">times in {{ summary.timezone }}</span>
    </div>

    <div v-if="!mobileView" class="vb-card">
      <b-table
        :busy="loading"
        :items="filteredItems"
        :fields="visibleFields"
        :sort-compare="tableSortCompare"
        :tbody-tr-class="rowClass"
        responsive
        show-empty
      >

        <template slot="table-busy">
          <div class="vb-busy"><i class="fa fa-spinner fa-spin"></i>&nbsp;Loading vehicles...</div>
        </template>

        <template slot="empty">
          <div class="vb-empty">
            <template v-if="filtersActive || searchActive">
              No vehicles match.
              <button type="button" class="vb-clear-btn" @click="clearFilters">Clear</button>
            </template>
            <template v-else>No vehicle data yet.</template>
          </div>
        </template>

        <!-- expand button: fetches the status history on open, then
             toggles the details row -->
        <template slot="details" slot-scope="row">
          <button
            type="button"
            class="vb-expand-btn"
            :class="{ 'vb-expand-btn--open': expanded.includes(row.item.name) }"
            aria-label="Show status history"
            :aria-expanded="expanded.includes(row.item.name) ? 'true' : 'false'"
            @click="toggleHistory(row)"
          >
            <i class="fa fa-chevron-right"></i>
          </button>
        </template>

        <template slot="name" slot-scope="row">
          <a class="vb-vehicle-link" target="_blank" :href="'/#/vehicle-track/' + tenant + '/' + row.item.name">{{ row.item.name }}</a>
        </template>

        <!-- wash status: chip pill; click opens the anchored picker for
             roles that may write -->
        <template slot="vehicle_status" slot-scope="row">
          <button
            v-if="canEditWash"
            type="button"
            class="vb-chip-btn"
            :class="{ 'vb-chip-btn--active': editing_cell === cellKey(row.item, 'vehicle_status') }"
            title="Update wash status"
            aria-haspopup="menu"
            :aria-expanded="editing_cell === cellKey(row.item, 'vehicle_status') ? 'true' : 'false'"
            :aria-label="'Change wash status for ' + row.item.name"
            :data-status-trigger="cellKey(row.item, 'vehicle_status')"
            @click="startEdit(row.item, 'vehicle_status', $event)"
          ><span class="vb-status-chip vb-status-chip--trigger" :class="washChipClass(row.item.vehicle_status)">{{ washChipLabel(row.item.vehicle_status) }}<i class="fa fa-caret-down vb-status-caret" aria-hidden="true"></i></span></button>
          <span v-else class="vb-status-chip" :class="washChipClass(row.item.vehicle_status)">{{ washChipLabel(row.item.vehicle_status) }}</span>
        </template>

        <!-- maintenance status: same pattern, different gate + options -->
        <template slot="maintenance_status" slot-scope="row">
          <button
            v-if="canEditMaint"
            type="button"
            class="vb-chip-btn"
            :class="{ 'vb-chip-btn--active': editing_cell === cellKey(row.item, 'maintenance_status') }"
            title="Update maintenance status"
            aria-haspopup="menu"
            :aria-expanded="editing_cell === cellKey(row.item, 'maintenance_status') ? 'true' : 'false'"
            :aria-label="'Change maint status for ' + row.item.name"
            :data-status-trigger="cellKey(row.item, 'maintenance_status')"
            @click="startEdit(row.item, 'maintenance_status', $event)"
          ><span class="vb-status-chip vb-status-chip--trigger" :class="maintChipClass(row.item.maintenance_status)">{{ maintChipLabel(row.item.maintenance_status) }}<i class="fa fa-caret-down vb-status-caret" aria-hidden="true"></i></span></button>
          <span v-else class="vb-status-chip" :class="maintChipClass(row.item.maintenance_status)">{{ maintChipLabel(row.item.maintenance_status) }}</span>
        </template>

        <template slot="notes" slot-scope="row">
          <b-form-input
            v-model="row.item.notes"
            size="sm"
            class="vb-notes-input"
            :class="{ 'vb-notes-input--saved': notes_saved === row.item.name }"
            :aria-label="'Notes for ' + row.item.name"
            @focus="noteFocus(row.item)"
            @blur="saveNotes(row.item)"
          />
        </template>

        <!-- departs-in: red is reserved for actual lateness, a sub-24h
             countdown is normal operations and stays default ink -->
        <template slot="next_mvmt_delta" slot-scope="row">
          <span :class="{ 'vb-late': departUrgency(row.item) === 'late', 'vb-soon': departUrgency(row.item) === 'soon' }">{{ row.item.next_mvmt_delta || '-' }}</span>
          <span
            v-for="(nm, idx) in row.item.next_mvmt_list || []"
            :key="'nd-' + idx"
            class="vb-stack-line"
            :class="{ 'vb-late': isLate(nm.next_mvmt_delta) }"
          >{{ nm.next_mvmt_delta }}</span>
        </template>

        <template slot="next_mvmt_time" slot-scope="row">
          {{ formatStamp(row.item.next_mvmt_time) }}
          <span
            v-for="(nm, idx) in row.item.next_mvmt_list || []"
            :key="'nt-' + idx"
            class="vb-stack-line"
          >{{ formatStamp(nm.next_mvmt_time) }}</span>
        </template>

        <!-- driver/id/return stack each upcoming booking on its own line, the
             way the standard vsm does, so a multi-movement row reads as one
             aligned line per booking across every next-movement column -->
        <template slot="next_mvmt_driver" slot-scope="row">
          {{ formatDrivers(row.item.next_mvmt_driver) }}
          <span
            v-for="(nm, idx) in row.item.next_mvmt_list || []"
            :key="'ndr-' + idx"
            class="vb-stack-line"
          >{{ formatDrivers(nm.next_mvmt_driver) }}</span>
        </template>

        <template slot="next_mvmt_id" slot-scope="row">
          {{ row.item.next_mvmt_id || '-' }}
          <span
            v-for="(nm, idx) in row.item.next_mvmt_list || []"
            :key="'nid-' + idx"
            class="vb-stack-line"
          >{{ nm.next_mvmt_id }}</span>
        </template>

        <template slot="next_mvmt_return_time" slot-scope="row">
          {{ formatStamp(row.item.next_mvmt_return_time) }}
          <span
            v-for="(nm, idx) in row.item.next_mvmt_list || []"
            :key="'nrt-' + idx"
            class="vb-stack-line"
          >{{ formatStamp(nm.next_mvmt_return_time) }}</span>
        </template>
        <template slot="current_mvmt_return_time" slot-scope="row">{{ formatStamp(row.item.current_mvmt_return_time) }}</template>

        <!-- the last_* stamps only exist once a vehicle_info row does,
             mirroring the legacy guards -->
        <template slot="last_dirty_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">{{ formatStamp(row.item.last_dirty_time) }}</span>
        </template>
        <template slot="last_cleaned_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">{{ formatStamp(row.item.last_cleaned_time) }}</span>
        </template>
        <template slot="last_washed_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">{{ formatStamp(row.item.last_washed_time) }}</span>
        </template>
        <template slot="last_returned_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">{{ formatStamp(row.item.last_returned_time) }}</span>
        </template>

        <!-- wheelchair / stay-and-do / tote render read-only here: the cm
             api has no tote write path and the legacy checkbox handler is
             broken, so the preview does not pretend they are editable -->
        <template slot="wheelchair" slot-scope="row">{{ row.item.wheelchair ? 'Yes' : '-' }}</template>
        <template slot="stayanddo" slot-scope="row">{{ row.item.stayanddo ? 'Yes' : '-' }}</template>
        <template slot="tote" slot-scope="row">{{ row.item.tote_label || (row.item.tote ? 'Yes' : '-') }}</template>

        <!-- status change history, fetched on expand -->
        <template slot="row-details" slot-scope="row">
          <div class="vb-history">
            <div v-if="row.item.history_error" class="vb-history-error">
              History did not load.
              <button type="button" class="vb-clear-btn" @click="fetchHistory(row.item.name)">Retry</button>
            </div>
            <table v-else-if="row.item.history && row.item.history.length" class="vb-history-table">
              <thead>
                <tr>
                  <th>Updated</th>
                  <th>By</th>
                  <th>Wash</th>
                  <th>Maint</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(h, idx) in row.item.history" :key="idx">
                  <td>{{ formatStamp(h.processed_time) }}</td>
                  <td>{{ h.updated_by || '-' }}</td>
                  <td>{{ statusLabel(h.vehicle_status) || '-' }}</td>
                  <td>{{ statusLabel(h.maintenance_status) || '-' }}</td>
                  <td>{{ h.notes || '-' }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else-if="row.item.history" class="vb-history-empty">No status history yet.</div>
            <div v-else class="vb-history-empty">Loading history...</div>
          </div>
        </template>

      </b-table>
    </div>

    <!-- mobile card layout: same data priority as the 3-column legacy
         phone view (identity, status, urgency) but built as cards so the
         secondary columns expand in place instead of scrolling off a
         shrunken table -->
    <div v-else class="vb-card-list">
      <div v-if="loading" class="vb-busy"><i class="fa fa-spinner fa-spin"></i>&nbsp;Loading vehicles...</div>
      <div v-else-if="!filteredItems.length" class="vb-empty">
        <template v-if="filtersActive || searchActive">No vehicles match.<button type="button" class="vb-clear-btn" @click="clearFilters">Clear</button></template>
        <template v-else>No vehicle data yet.</template>
      </div>
      <div v-for="item in filteredItems" :key="item.name" class="vb-vcard" :class="'vb-vcard--' + rowState(item)">
        <div class="vb-vcard-head">
          <a class="vb-vehicle-link" target="_blank" :href="'/#/vehicle-track/' + tenant + '/' + item.name">{{ item.name }}</a>
          <!-- depart block only renders when a movement is scheduled; an idle
               vehicle shows just its name rather than a hollow "Departs in -" -->
          <div v-if="item.next_mvmt_delta" class="vb-vcard-depart">
            <div class="vb-vcard-depart-main">
              <span class="vb-vcard-depart-label">Departs in</span>
              <span
                class="vb-vcard-depart-val"
                :class="{
                  'vb-late': departUrgency(item) === 'late',
                  'vb-soon': departUrgency(item) === 'soon',
                  'vb-vcard-depart-val--distant': departIsDistant(item)
                }"
              >{{ item.next_mvmt_delta }}</span>
            </div>
            <!-- precise depart stamp under the countdown: a fixed landmark the
                 relative "19h 13m" can't give, and it survives a printed or
                 screenshotted card where the moment of capture is lost -->
            <span v-if="departStamp(item)" class="vb-vcard-depart-when">{{ departStamp(item) }}</span>
          </div>
        </div>

        <div class="vb-vcard-actionrow">
          <!-- wash cell: stacked caption + fixed-width chip. the whole cell is
               the tap target, and the two status cells share equal grid columns
               so the boxes land in the same spot card-to-card (muscle memory).
               an unset editable status shows a ghost "Set" placeholder. -->
          <button
            v-if="canEditWash"
            type="button"
            class="vb-chip-btn vb-vcard-statusctl"
            :class="{ 'vb-chip-btn--active': editing_cell === cellKey(item, 'vehicle_status') }"
            aria-haspopup="menu"
            :aria-expanded="editing_cell === cellKey(item, 'vehicle_status') ? 'true' : 'false'"
            :aria-label="'Change wash status for ' + item.name"
            :data-status-trigger="cellKey(item, 'vehicle_status')"
            @click="startEdit(item, 'vehicle_status', $event)"
          ><span class="vb-vcard-chip-tag">Wash</span><span class="vb-status-chip vb-status-chip--trigger" :class="[washChipClass(item.vehicle_status), { 'vb-status-chip--set': !item.vehicle_status }]">{{ item.vehicle_status ? washChipLabel(item.vehicle_status) : 'Set' }}<i class="fa fa-caret-down vb-status-caret" aria-hidden="true"></i></span></button>
          <span v-else class="vb-vcard-statusctl"><span class="vb-vcard-chip-tag">Wash</span><span class="vb-status-chip" :class="washChipClass(item.vehicle_status)">{{ washChipLabel(item.vehicle_status) }}</span></span>

          <!-- maint cell -->
          <button
            v-if="canEditMaint"
            type="button"
            class="vb-chip-btn vb-vcard-statusctl"
            :class="{ 'vb-chip-btn--active': editing_cell === cellKey(item, 'maintenance_status') }"
            aria-haspopup="menu"
            :aria-expanded="editing_cell === cellKey(item, 'maintenance_status') ? 'true' : 'false'"
            :aria-label="'Change maint status for ' + item.name"
            :data-status-trigger="cellKey(item, 'maintenance_status')"
            @click="startEdit(item, 'maintenance_status', $event)"
          ><span class="vb-vcard-chip-tag">Maint</span><span class="vb-status-chip vb-status-chip--trigger" :class="[maintChipClass(item.maintenance_status), { 'vb-status-chip--set': !item.maintenance_status }]">{{ item.maintenance_status ? maintChipLabel(item.maintenance_status) : 'Set' }}<i class="fa fa-caret-down vb-status-caret" aria-hidden="true"></i></span></button>
          <span v-else class="vb-vcard-statusctl"><span class="vb-vcard-chip-tag">Maint</span><span class="vb-status-chip" :class="maintChipClass(item.maintenance_status)">{{ maintChipLabel(item.maintenance_status) }}</span></span>

          <!-- expand toggle rides the right edge of the status row, reclaiming
               its empty half and dropping a full button band per card -->
          <button
            type="button"
            class="vb-vcard-more"
            :aria-expanded="expanded.includes(item.name) ? 'true' : 'false'"
            @click="toggleHistoryByName(item)"
          >{{ expanded.includes(item.name) ? 'Hide details' : 'Details' }}<i class="fa" :class="expanded.includes(item.name) ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i></button>
        </div>

        <div v-if="expanded.includes(item.name)" class="vb-vcard-detail">
          <div v-for="f in mobileSecondaryFields" :key="f.key" class="vb-vcard-row">
            <span class="vb-vcard-row-label">{{ f.label }}</span>
            <span class="vb-vcard-row-val">{{ cellDisplay(item, f.key) }}</span>
          </div>
          <div class="vb-vcard-row vb-vcard-notes">
            <span class="vb-vcard-row-label">Notes</span>
            <b-form-input
              v-model="item.notes"
              size="sm"
              class="vb-notes-input"
              :class="{ 'vb-notes-input--saved': notes_saved === item.name }"
              :aria-label="'Notes for ' + item.name"
              @focus="noteFocus(item)"
              @blur="saveNotes(item)"
            />
          </div>
          <!-- history as stacked entries so it never overflows the card the
               way the desktop table would on a phone -->
          <div class="vb-vcard-history">
            <div v-if="item.history_error" class="vb-history-error">History did not load.<button type="button" class="vb-clear-btn" @click="fetchHistory(item.name)">Retry</button></div>
            <template v-else-if="item.history && item.history.length">
              <div v-for="(h, idx) in item.history" :key="idx" class="vb-hist-entry">
                <div class="vb-hist-top">
                  <span class="vb-hist-when">{{ formatStamp(h.processed_time) }}</span>
                  <span class="vb-hist-by">{{ h.updated_by || '-' }}</span>
                </div>
                <div class="vb-hist-what">
                  <span class="vb-status-chip" :class="washChipClass(h.vehicle_status)">{{ statusLabel(h.vehicle_status) || '-' }}</span>
                  <span class="vb-status-chip" :class="maintChipClass(h.maintenance_status)">{{ statusLabel(h.maintenance_status) || '-' }}</span>
                </div>
                <div v-if="h.notes" class="vb-hist-note">{{ h.notes }}</div>
              </div>
            </template>
            <div v-else-if="item.history" class="vb-history-empty">No status history yet.</div>
            <div v-else class="vb-history-empty">Loading history...</div>
          </div>
        </div>
      </div>
    </div>
    </template>

    <!-- legend + how-to-read help. the key is drawn with the same chips and
         colors the board renders, so it always matches what is on screen.
         the triage-rail section is new to this board (the legacy vsm had no
         row coloring to explain). -->
    <b-modal v-model="show_info" title="About this board" size="lg" ok-only ok-title="Got it" hide-header-close>
      <div class="vb-info">
        <section class="vb-info-sec">
          <h6 class="vb-info-h">Reading the board</h6>
          <p class="vb-info-p">A colored edge marks rows that need a look. The most urgent reason wins.</p>
          <ul class="vb-info-legend">
            <li>
              <span class="vb-info-dot vb-info-dot--late"></span>
              <span><strong>Late or out of service.</strong> Overdue to depart, out of service, or an unsafe DVIR. Needs action now.</span>
            </li>
            <li>
              <span class="vb-info-dot vb-info-dot--attention"></span>
              <span><strong>Heads up.</strong> Leaving soon, dirty, in PM, or a pending DVIR.</span>
            </li>
            <li>
              <span class="vb-info-dot vb-info-dot--ready"></span>
              <span><strong>Available.</strong> Clean and ready to roll.</span>
            </li>
          </ul>
        </section>

        <section class="vb-info-sec">
          <h6 class="vb-info-h">Wash status</h6>
          <ul class="vb-info-chips">
            <li v-for="row in washLegend" :key="'wl-' + row.value">
              <span class="vb-status-chip" :class="washChipClass(row.value)">{{ washChipLabel(row.value) }}</span>
              <span class="vb-info-desc">{{ row.desc }}</span>
            </li>
          </ul>
        </section>

        <section class="vb-info-sec">
          <h6 class="vb-info-h">Maintenance status</h6>
          <ul class="vb-info-chips">
            <li v-for="row in maintLegend" :key="'ml-' + row.value">
              <span class="vb-status-chip" :class="maintChipClass(row.value)">{{ maintChipLabel(row.value) }}</span>
              <span class="vb-info-desc">{{ row.desc }}</span>
            </li>
          </ul>
        </section>

        <section class="vb-info-sec">
          <h6 class="vb-info-h">Filters &amp; refresh</h6>
          <p class="vb-info-p"><strong>Available</strong> counts vehicles that are {{ washedEnabled ? 'ready' : 'clean' }} and have a ready maintenance status.</p>
          <p class="vb-info-p">With auto-refresh on, the board reloads every {{ refresh_secs }} seconds. It holds when the tab is in the background or while you are editing a status or note, so nothing shifts under your hands.</p>
        </section>
      </div>
    </b-modal>

    <!-- one floating status picker for the whole board, anchored to the
         chip that opened it. position: fixed so it escapes the table's
         overflow scrollport and never reflows the neighboring chip. -->
    <div
      v-if="editing_cell"
      ref="pickerPop"
      class="vb-picker-pop"
      :style="picker_style"
      role="menu"
      :aria-label="picker_field === 'maintenance_status' ? 'Set maintenance status' : 'Set wash status'"
      @keydown.esc="cancelEdit(true)"
    >
      <button
        v-for="opt in pickerOptions"
        :key="opt.value"
        type="button"
        class="vb-status-opt"
        :class="{ 'vb-status-opt--current': picker_item && picker_item[picker_field] === opt.value }"
        role="menuitemradio"
        :aria-checked="picker_item && picker_item[picker_field] === opt.value ? 'true' : 'false'"
        @click="setStatus(picker_item, picker_field, opt.value)"
      ><span class="vb-status-chip" :class="pickerChipClass(opt.value)">{{ opt.text }}</span><i v-if="picker_item && picker_item[picker_field] === opt.value" class="fa fa-check vb-status-opt-check" aria-hidden="true"></i></button>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
// moment-timezone (not bare moment) so formatStamp can render each stamp in
// the tenant's zone; the singleton is already augmented app-wide via main.js
import moment from 'moment-timezone'
import { get_vehicle_info, get_vehicle_info_fasttrak, update_vehicle_status, get_vehicle_info_history } from '../networking/apis'
import { get_tbn_vehicle_info, tbn_update_vehicle_status } from '../networking/tbn_apis'
import { tenantContextDiffers, tenantDisplayName } from '../utils/tenant_context'
import { statusLabel, statusOptions } from '@/utils/vehicle_status_labels'

// '-' placeholder for the plain-text columns rendered by b-table's default
// cell; arrays (multi-driver) join into one line
const dash = value => {
  if (value === null || value === undefined || value === '') return '-'
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-'
  return value
}

// column catalog: keys mirrored from vehicle_status_maint.vue's fields
// array with the redesign label fixes (sentence case, shorter wash/maint
// headers). only the columns the team actually scans are sortable.
const FIELDS = [
  { key: 'details', label: '', sortable: false },
  { key: 'name', label: 'Vehicle', sortable: true },
  { key: 'vehicle_status', label: 'Wash', sortable: true },
  { key: 'maintenance_status', label: 'Maint', sortable: true },
  { key: 'fleetio_status', label: 'Fleetio status', formatter: dash },
  { key: 'rta_status', label: 'RTA status', formatter: dash },
  { key: 'rta_work_order_status', label: 'RTA WO status', formatter: dash },
  { key: 'last_dirty_time', label: 'Dirty' },
  { key: 'last_cleaned_time', label: 'Cleaned' },
  { key: 'last_cleaned_number', label: 'Cleaned #', formatter: dash },
  { key: 'last_washed_time', label: 'Washed' },
  { key: 'last_washed_number', label: 'Washed #', formatter: dash },
  { key: 'last_returned_time', label: 'Last return' },
  { key: 'current_mvmt_finish_base', label: 'Current finish', formatter: dash },
  { key: 'current_mvmt_return_time', label: 'Current return' },
  { key: 'next_mvmt_return_time', label: 'Next return' },
  { key: 'next_mvmt_start_base', label: 'Next start', formatter: dash },
  { key: 'next_mvmt_driver', label: 'Next driver', formatter: dash },
  { key: 'next_mvmt_id', label: 'Next ID', formatter: dash },
  { key: 'next_mvmt_time', label: 'Next depart' },
  { key: 'next_mvmt_delta', label: 'Departs in', sortable: true },
  { key: 'wheelchair', label: 'WC' },
  { key: 'stayanddo', label: 'S&D' },
  { key: 'tote', label: 'Tote' },
  { key: 'notes', label: 'Notes' },
]

// fallback column set when the tenant has not configured visible_fields:
// the status columns the surface is named for, the next-movement scan
// set, and notes
const DEFAULT_VISIBLE_KEYS = [
  'name',
  'vehicle_status',
  'maintenance_status',
  'next_mvmt_id',
  'next_mvmt_driver',
  'next_mvmt_time',
  'next_mvmt_delta',
  'notes',
]

// chip color per wash status, matching the standard VSM's hue language:
// clean = cyan, washed = sea-green, dirty = brown, out = purple, ready = green
const WASH_CHIP_CLASSES = {
  READY: 'vb-chip--green',
  CLEANED: 'vb-chip--cyan',
  WASHED: 'vb-chip--teal',
  DIRTY: 'vb-chip--brown',
  OUT: 'vb-chip--purple',
}

const MAINT_CHIP_CLASSES = {
  READY: 'vb-chip--green',
  PM: 'vb-chip--blue',
  OOS: 'vb-chip--red',
  PENDING_DVIR: 'vb-chip--amber',
  UNSAFE_DVIR: 'vb-chip--red',
}

// one-line meaning per status for the info modal legend. order mirrors the
// wash/maint flow; the washed_enabled trim is applied in the computed.
const WASH_LEGEND = [
  { value: 'READY', desc: 'cleaned and washed, ready for service (set automatically)' },
  { value: 'CLEANED', desc: 'interior has been cleaned (set by the team)' },
  { value: 'WASHED', desc: 'exterior has been washed (set by the team)' },
  { value: 'DIRTY', desc: 'back at the yard and not yet cleaned (set automatically)' },
  { value: 'OUT', desc: 'out on a booking (set automatically)' },
]
const MAINT_LEGEND = [
  { value: 'READY', desc: 'DVIRs clear, good to roll (set automatically)' },
  { value: 'PENDING_DVIR', desc: 'a DVIR is open and awaiting review (set automatically)' },
  { value: 'UNSAFE_DVIR', desc: 'an unsafe DVIR was reported (set automatically)' },
  { value: 'PM', desc: 'planned or preventative maintenance (set by the team)' },
  { value: 'OOS', desc: 'out of service (set by the team)' },
]

// auto-refresh cadence, matching the legacy standalone vsm's 60s reload
const REFRESH_SECS = 60

// next_mvmt_ms is an absolute epoch in seconds; anything at/above this
// sentinel means "no scheduled movement" (the api sorts those last)
const MS_SENTINEL = 1e15
// a departure inside this window is "soon" — amber on the card rail. the
// delta string still carries the human-readable countdown; this only drives
// the at-a-glance triage color.
const SOON_SECS = 2 * 60 * 60

export default {
  name: 'vsm_board',
  data() {
    return {
      loading: false,
      refreshing: false,
      error: '',
      // true after a 404/empty load — the fleet has no vehicles to show
      // (typically no telematics feed), rendered as a calm fact not an error
      noData: false,
      // true under the mobile breakpoint; swaps the table for the card list
      mobileView: false,
      summary: null,
      items_full: [],
      selected_base: '',
      wash_filters: [],
      maint_filters: [],
      tag_filters: [],
      // free-text vehicle/driver find, applied on top of the chip filters
      search: '',

      // anchored status picker: which cell's options are open (one at a
      // time), plus the row/field it targets and the fixed-position style
      // computed from the trigger chip. rendered once at the root as a
      // floating popover, so opening it never reflows the sibling chip.
      editing_cell: null,
      picker_item: null,
      picker_field: null,
      picker_style: null,
      notes_original: null,

      // vehicle name whose notes input just saved, for the brief
      // confirmation flash
      notes_saved: '',
      notes_saved_timer: null,
      // vehicle name whose notes input is currently focused; auto-refresh
      // checks this so a soft reload never stomps a half-typed note
      notes_editing: '',

      // auto-refresh: a periodic soft reload for the wall-board use case.
      // on by default at the legacy cadence; the timer is paused per-tick
      // when it would waste a call or disrupt an edit (see autoRefresh)
      auto_refresh: true,
      refresh_secs: REFRESH_SECS,
      refresh_timer: null,
      // epoch ms of the last successful load, surfaced as "updated h:mm:ss"
      last_updated: null,
      // epoch ms stamped right before window.print(), for the print header
      printed_at: null,

      // the "About this board" legend/help modal
      show_info: false,

      // vehicle names with their history panel open
      expanded: [],

      // sequence guard so only the latest in-flight fetch lands
      fetch_seq: 0,
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant() {
      return this.$route.query.tenant || (this.user && this.user.tenant)
    },
    // every booking source returns the same {summary, results} contract, but
    // the read/write endpoints differ. fasttrak has a parallel route on the cm
    // api; tbn lives in its own api module. dispatch on the tenant type the
    // navbar already exposes via user.types so deep-links resolve too.
    vsmSource() {
      const types = this.user && this.user.types
      const type = (types && this.tenant && types[this.tenant]) || ''
      if (type === 'FASTTRAK') return 'fasttrak'
      if (type === 'TBN') return 'tbn'
      return 'cm'
    },
    // admins deep-link with ?tenant=, so name the tenant whenever it
    // differs from the navbar selection
    contextTenantName() {
      if (!tenantContextDiffers(this.$route, this.user)) return ''
      return tenantDisplayName(this.user, this.tenant)
    },
    washedEnabled() {
      return !!(this.summary && this.summary.washed_enabled)
    },
    baseOptions() {
      const opts = (this.summary && this.summary.bases) || []
      // client-side label fix only: the server option list is shared with
      // the legacy page and its first entry arrives as 'All Bases'
      return opts.map((opt, idx) => {
        if (idx === 0 && opt && opt.text === 'All Bases') return { ...opt, text: 'All bases' }
        return opt
      })
    },
    filtersActive() {
      return this.wash_filters.length > 0 || this.maint_filters.length > 0 || this.tag_filters.length > 0
    },
    searchActive() {
      return !!(this.search && this.search.trim())
    },
    // available mirrors the legacy available_cnt: wash READY (or CLEANED
    // when the tenant has no washed step) plus maint READY
    countAvailable() {
      const washState = this.washedEnabled ? 'READY' : 'CLEANED'
      return this.items_full.filter(item => item.vehicle_status === washState && item.maintenance_status === 'READY').length
    },
    // available renders as its own standalone chip ahead of this group,
    // so the wash group label only covers true wash statuses
    washChips() {
      // color mirrors WASH_CHIP_CLASSES so each filter pill matches its
      // status chip on the rows
      const chips = []
      if (this.washedEnabled) chips.push({ value: 'ready', label: 'Ready', color: 'green', count: this.countWash('READY') })
      chips.push({ value: 'cleaned', label: 'Clean', color: 'cyan', count: this.countWash('CLEANED') })
      if (this.washedEnabled) chips.push({ value: 'washed', label: 'Washed', color: 'teal', count: this.countWash('WASHED') })
      chips.push({ value: 'dirty', label: 'Dirty', color: 'brown', count: this.countWash('DIRTY') })
      chips.push({ value: 'out', label: 'Out', color: 'purple', count: this.countWash('OUT') })
      return chips
    },
    maintChips() {
      // color mirrors MAINT_CHIP_CLASSES
      const chips = [
        { value: 'ready', label: 'Ready', color: 'green', count: this.countMaint('READY') },
        { value: 'pm', label: 'PM', color: 'blue', count: this.countMaint('PM') },
        { value: 'oos', label: 'OOS', color: 'red', count: this.countMaint('OOS') },
      ]
      // system-written dvir statuses are not in the manual dropdown but do
      // occur on rows; chip them only when present so the counts cover the
      // whole fleet without cluttering tenants that never see them
      const pending = this.countMaint('PENDING_DVIR')
      const unsafe = this.countMaint('UNSAFE_DVIR')
      if (pending > 0) chips.push({ value: 'pending_dvir', label: 'Pending DVIR', color: 'amber', count: pending })
      if (unsafe > 0) chips.push({ value: 'unsafe_dvir', label: 'Unsafe DVIR', color: 'red', count: unsafe })
      return chips
    },
    // distinct tag set across the loaded fleet, sorted, with stable per-tag
    // counts from the unfiltered set. derived from the rows (not summary) so
    // it follows whatever vehicle_tags the feed actually carries; empty on
    // feeds with no tags, which collapses the tags control to nothing.
    tagChips() {
      const seen = {}
      this.items_full.forEach(item => {
        this.itemTags(item).forEach(t => { seen[t] = (seen[t] || 0) + 1 })
      })
      return Object.keys(seen).sort().map(t => ({ value: t, label: t, count: seen[t] }))
    },
    // the two chip groups AND together; within a group membership is OR,
    // with 'available' special-cased like the legacy filter_items
    filteredItems() {
      let items = this.items_full
      if (this.wash_filters.length > 0) {
        const upper = this.wash_filters.map(s => s.toUpperCase())
        const wantAvailable = this.wash_filters.includes('available')
        const washState = this.washedEnabled ? 'READY' : 'CLEANED'
        items = items.filter(item => {
          if (wantAvailable && item.vehicle_status === washState && item.maintenance_status === 'READY') return true
          return upper.includes(item.vehicle_status)
        })
      }
      if (this.maint_filters.length > 0) {
        const upper = this.maint_filters.map(s => s.toUpperCase())
        items = items.filter(item => upper.includes(item.maintenance_status))
      }
      // tag group ands with wash/maint; within the group membership is or
      // (the row carries any one of the selected tags). exact-token match so
      // "oc" never matches "oceanside".
      if (this.tag_filters.length > 0) {
        items = items.filter(item => {
          const tags = this.itemTags(item)
          return this.tag_filters.some(t => tags.indexOf(t) !== -1)
        })
      }
      if (this.searchActive) {
        const q = this.search.trim().toLowerCase()
        items = items.filter(item => this.matchesSearch(item, q))
      }
      return items
    },
    // tenant-configured column list when present, the default scan set
    // otherwise; details + name always lead, unknown keys are dropped
    visibleFields() {
      const byKey = {}
      FIELDS.forEach(field => { byKey[field.key] = field })

      let keys
      if (this.summary && this.summary.visible_fields) {
        keys = this.summary.visible_fields.split(',').map(k => k.trim()).filter(k => byKey[k])
      } else {
        keys = DEFAULT_VISIBLE_KEYS.filter(k => byKey[k])
      }

      // the board is built around wash + maint; always surface both status
      // columns (right after name) even when the tenant's column config
      // omits them
      const lead = { details: 1, name: 1, vehicle_status: 1, maintenance_status: 1 }
      keys = keys.filter(k => !lead[k])
      return [byKey['details'], byKey['name'], byKey['vehicle_status'], byKey['maintenance_status'], ...keys.map(k => byKey[k])]
    },
    // on the card layout the head shows name + the two status chips +
    // departs-in; everything else the tenant configured drops into the
    // expandable detail, notes excluded (it renders as its own field there)
    mobileSecondaryFields() {
      // next_mvmt_delta + next_mvmt_time render in the card head now (the
      // "Departs in" countdown and its precise stamp), so keep them out of the
      // expandable detail to avoid showing the depart time twice
      const skip = { details: 1, name: 1, vehicle_status: 1, maintenance_status: 1, next_mvmt_delta: 1, next_mvmt_time: 1, notes: 1 }
      return this.visibleFields.filter(f => !skip[f.key])
    },
    // edit gates mirror the legacy page's role checks
    canEditWash() {
      const role = this.user && this.user.role
      return role !== 'dispatch' && role !== 'garage'
    },
    canEditMaint() {
      const role = this.user && this.user.role
      return role !== 'dispatch' && role !== 'wash-bay'
    },
    // built fresh per render so the washed_enabled trim never mutates a
    // shared array (the legacy page filters its options in place)
    washStatusOptions() {
      const opts = statusOptions(['READY', 'CLEANED', 'WASHED', 'DIRTY', 'OUT'])
      if (!this.washedEnabled) return opts.filter(o => o.value !== 'READY' && o.value !== 'WASHED')
      return opts
    },
    maintStatusOptions() {
      return statusOptions(['READY', 'PM', 'OOS'])
    },
    // the option set for whichever cell's picker is currently open
    pickerOptions() {
      return this.picker_field === 'maintenance_status' ? this.maintStatusOptions : this.washStatusOptions
    },

    // last-load stamp in the tenant's timezone, so it reads in the same zone
    // as every other time on the board (see formatStamp)
    updatedAt() {
      if (!this.last_updated) return ''
      const tz = (this.summary && this.summary.timezone) || ''
      const m = moment(this.last_updated)
      return (tz ? m.tz(tz) : m).format('h:mm:ss a')
    },
    autoRefreshTitle() {
      return this.auto_refresh
        ? 'Auto-refresh is on, every ' + this.refresh_secs + ' seconds. Click to pause.'
        : 'Auto-refresh is off. Click to resume.'
    },
    printedStamp() {
      return this.printed_at ? moment(this.printed_at).format('MMM D, YYYY h:mm a') : ''
    },
    // legend rows for the info modal. wash honors the washed_enabled trim so
    // the key matches the statuses this tenant actually uses
    washLegend() {
      if (this.washedEnabled) return WASH_LEGEND
      return WASH_LEGEND.filter(row => row.value !== 'READY' && row.value !== 'WASHED')
    },
    maintLegend() {
      return MAINT_LEGEND
    },
  },

  watch: {
    // the navbar switcher rewrites ?tenant= in place without remounting,
    // so reset the tenant-scoped state and refetch
    tenant() {
      this.selected_base = ''
      this.wash_filters = []
      this.maint_filters = []
      this.tag_filters = []
      this.search = ''
      this.summary = null
      this.items_full = []
      this.error = ''
      this.noData = false
      this.cancelEdit()
      this.load_data()
    },
  },

  mounted() {
    this.setupMobileQuery()
    // print path hides the app chrome via this body class while the board
    // is mounted, mirroring the trip-sheet wall board
    document.body.classList.add('vsm-board-print')
    document.addEventListener('visibilitychange', this.onVisibility)
    this.load_data()
    this.startAutoRefresh()
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocClick, true)
    window.removeEventListener('scroll', this.onWindowScroll, true)
    window.removeEventListener('resize', this.onWindowResize, true)
    document.removeEventListener('visibilitychange', this.onVisibility)
    document.body.classList.remove('vsm-board-print')
    if (this.notes_saved_timer) clearTimeout(this.notes_saved_timer)
    this.stopAutoRefresh()
    this.teardownMobileQuery()
  },

  methods: {
    // opts.soft = a background auto-refresh: merge rows in place so open
    // history panels and a focused note survive (see mergeResults). a hard
    // load (manual refresh, base/tenant change, first paint) replaces the
    // rows and resets those, since the user asked for fresh state.
    async load_data(opts = {}) {
      if (!this.tenant) return
      const soft = !!(opts && opts.soft === true)
      const seq = ++this.fetch_seq
      this.refreshing = true
      if (!this.summary) this.loading = true
      try {
        const fetch_vehicle_info =
          this.vsmSource === 'tbn' ? get_tbn_vehicle_info
          : this.vsmSource === 'fasttrak' ? get_vehicle_info_fasttrak
          : get_vehicle_info
        const { value: { data = {} } = {} } = await fetch_vehicle_info(this.tenant, this.selected_base)
        if (seq !== this.fetch_seq) return
        this.summary = data.summary || null
        this.error = ''
        this.noData = false
        this.last_updated = Date.now()
        if (soft) {
          this.mergeResults(data.results || [])
        } else {
          this.items_full = data.results || []
          // a hard reload replaces the row objects, so open history panels
          // and any in-flight cell edit reset rather than point at stale rows
          this.expanded = []
          this.cancelEdit()
        }
      } catch (e) {
        if (seq === this.fetch_seq) {
          // the roster is sourced from the telematics feed, so a fleet with
          // no connected provider 404s. show that as the calm no-data fact
          // and reserve the retryable error banner for genuine failures.
          const status = e && e.response && e.response.status
          if (status === 404) {
            this.noData = true
            this.error = ''
            this.summary = null
            this.items_full = []
          } else {
            // a genuine failure on a tenant that previously 404'd must clear
            // noData, or the calm card and the error banner stack together
            this.noData = false
            this.error = 'The vehicle data did not load. Check your connection and retry.'
          }
        }
      } finally {
        if (seq === this.fetch_seq) {
          this.loading = false
          this.refreshing = false
        }
      }
    },

    // soft-merge a fresh roster into the current rows: reuse each existing
    // row object by name so an open history panel and a focused notes input
    // survive the refresh, while following the new ordering and field values.
    // rows that drop out of the feed fall away; new rows come in as-is.
    mergeResults(results) {
      const prev = {}
      this.items_full.forEach(row => { prev[row.name] = row })
      this.items_full = results.map(incoming => {
        const existing = prev[incoming.name]
        if (!existing) return incoming
        Object.keys(incoming).forEach(key => {
          // never stomp a note the user is actively typing
          if (key === 'notes' && this.notes_editing === existing.name) return
          this.$set(existing, key, incoming[key])
        })
        return existing
      })
      // close history panels for vehicles no longer in the feed
      const present = {}
      results.forEach(row => { present[row.name] = true })
      this.expanded = this.expanded.filter(name => present[name])
    },

    // periodic background reload for the wall-board use case
    startAutoRefresh() {
      this.stopAutoRefresh()
      if (this.auto_refresh && this.refresh_secs > 0) {
        this.refresh_timer = setInterval(this.autoRefresh, this.refresh_secs * 1000)
      }
    },
    stopAutoRefresh() {
      if (this.refresh_timer) {
        clearInterval(this.refresh_timer)
        this.refresh_timer = null
      }
    },
    // one tick: defer rather than disrupt. a hidden tab, an in-flight fetch,
    // an open status picker, or a note being edited all wait for the next
    // interval, so the refresh never fights the user or wastes a call.
    autoRefresh() {
      if (!this.auto_refresh) return
      if (typeof document !== 'undefined' && document.hidden) return
      if (this.refreshing || this.loading) return
      if (this.editing_cell) return
      if (this.notes_editing) return
      this.load_data({ soft: true })
    },
    toggleAutoRefresh() {
      this.auto_refresh = !this.auto_refresh
      if (this.auto_refresh) {
        this.startAutoRefresh()
        // resuming should feel immediate, not wait a full interval
        this.autoRefresh()
      } else {
        this.stopAutoRefresh()
      }
    },
    // catch a tab returning to the foreground so a board left in the
    // background snaps current the moment it is looked at again
    onVisibility() {
      if (typeof document !== 'undefined' && !document.hidden) this.autoRefresh()
    },

    printPage() {
      // stamp the print header, let it paint, then open the print dialog
      this.printed_at = Date.now()
      this.$nextTick(() => window.print())
    },

    countWash(status) {
      return this.items_full.filter(item => item.vehicle_status === status).length
    },
    countMaint(status) {
      return this.items_full.filter(item => item.maintenance_status === status).length
    },
    // vehicle_tags arrives as a comma-delimited string ("sd,charter"); split
    // to trimmed, non-empty tokens. absent on non-samsara feeds, so guard.
    itemTags(item) {
      if (!item.vehicle_tags) return []
      return String(item.vehicle_tags).split(',').map(t => t.trim()).filter(Boolean)
    },
    countTag(tag) {
      return this.items_full.filter(item => this.itemTags(item).indexOf(tag) !== -1).length
    },

    toggleWashFilter(value) {
      if (this.wash_filters.includes(value)) {
        this.wash_filters = this.wash_filters.filter(v => v !== value)
      } else {
        this.wash_filters = [...this.wash_filters, value]
      }
    },
    toggleMaintFilter(value) {
      if (this.maint_filters.includes(value)) {
        this.maint_filters = this.maint_filters.filter(v => v !== value)
      } else {
        this.maint_filters = [...this.maint_filters, value]
      }
    },
    toggleTagFilter(value) {
      if (this.tag_filters.includes(value)) {
        this.tag_filters = this.tag_filters.filter(v => v !== value)
      } else {
        this.tag_filters = [...this.tag_filters, value]
      }
    },
    clearFilters() {
      this.wash_filters = []
      this.maint_filters = []
      this.tag_filters = []
      this.search = ''
    },
    // clear search and keep focus in the field, so keyboard/AT users are not
    // dropped to <body> when the v-if'd clear button unmounts itself
    clearSearch() {
      this.search = ''
      this.$nextTick(() => {
        if (this.$refs.searchInput && this.$refs.searchInput.focus) this.$refs.searchInput.focus()
      })
    },

    // free-text match across the fields the legacy board searches: vehicle
    // name, the next-movement driver(s), wash/maint status, bases, id, notes
    matchesSearch(item, q) {
      const hit = v => {
        if (v === null || v === undefined) return false
        if (Array.isArray(v)) return v.some(hit)
        return String(v).toLowerCase().indexOf(q) !== -1
      }
      if (hit(item.name)) return true
      if (hit(item.next_mvmt_driver)) return true
      if (hit(item.next_mvmt_start_base) || hit(item.start_bases)) return true
      if (hit(item.next_mvmt_id)) return true
      if (hit(item.notes)) return true
      if (hit(item.vehicle_tags)) return true
      if (hit(this.washChipLabel(item.vehicle_status)) || hit(this.maintChipLabel(item.maintenance_status))) return true
      // multi-movement rows carry additional drivers/ids in next_mvmt_list
      return (item.next_mvmt_list || []).some(nm => hit(nm.next_mvmt_driver) || hit(nm.next_mvmt_id))
    },

    // mobile breakpoint, reactive to rotation/resize so the layout is not
    // pinned at mount the way a user-agent sniff would be
    setupMobileQuery() {
      if (typeof window === 'undefined' || !window.matchMedia) return
      this._mql = window.matchMedia('(max-width: 640px)')
      this.mobileView = this._mql.matches
      this._mqlHandler = e => { this.mobileView = e.matches }
      if (this._mql.addEventListener) this._mql.addEventListener('change', this._mqlHandler)
      else this._mql.addListener(this._mqlHandler)
    },
    teardownMobileQuery() {
      if (!this._mql) return
      if (this._mql.removeEventListener) this._mql.removeEventListener('change', this._mqlHandler)
      else this._mql.removeListener(this._mqlHandler)
    },

    // card-mode history toggle: there is no b-table row to flip, so panel
    // visibility rides on the same expanded[] the fetch guard already uses
    toggleHistoryByName(item) {
      const name = item.name
      if (this.expanded.includes(name)) {
        this.expanded = this.expanded.filter(n => n !== name)
        return
      }
      this.expanded = [...this.expanded, name]
      this.fetchHistory(name)
    },

    // one display value for a secondary field in the card detail, covering
    // the timestamp, boolean, and plain-text cases the table renders
    // through its per-column slots
    cellDisplay(item, key) {
      const stampKeys = {
        last_dirty_time: 1, last_cleaned_time: 1, last_washed_time: 1,
        last_returned_time: 1, current_mvmt_return_time: 1,
        next_mvmt_return_time: 1, next_mvmt_time: 1,
      }
      if (stampKeys[key]) {
        const guarded = key === 'last_dirty_time' || key === 'last_cleaned_time' || key === 'last_washed_time' || key === 'last_returned_time'
        if (guarded && !item.vehicle_info) return '-'
        return this.formatStamp(item[key])
      }
      if (key === 'wheelchair') return item.wheelchair ? 'Yes' : '-'
      if (key === 'stayanddo') return item.stayanddo ? 'Yes' : '-'
      if (key === 'tote') return item.tote_label || (item.tote ? 'Yes' : '-')
      return dash(item[key])
    },

    // shared status labels (also called directly by the history rows);
    // the chip wrappers keep the '-' placeholder for missing values
    statusLabel,
    washChipLabel(status) {
      return status ? statusLabel(status) : '-'
    },
    washChipClass(status) {
      return WASH_CHIP_CLASSES[status] || 'vb-chip--gray'
    },
    maintChipLabel(status) {
      return status ? statusLabel(status) : '-'
    },
    maintChipClass(status) {
      return MAINT_CHIP_CLASSES[status] || 'vb-chip--gray'
    },
    // chip tint for an option in the open picker (wash vs maint palette)
    pickerChipClass(value) {
      return this.picker_field === 'maintenance_status' ? this.maintChipClass(value) : this.washChipClass(value)
    },

    // at-a-glance triage that colors the card's left rail (and the table
    // row's left edge). most urgent wins. undefined maint is treated as
    // neutral, mirroring how countAvailable already ignores it, so a tenant
    // that does not track maintenance never lights up amber/red for it.
    rowState(item) {
      const maint = item.maintenance_status
      const wash = item.vehicle_status
      // out of service or already overdue → red
      if (maint === 'OOS' || maint === 'UNSAFE_DVIR') return 'late'
      if (this.isLate(item.next_mvmt_delta)) return 'late'
      const ms = item.next_mvmt_ms
      const scheduled = typeof ms === 'number' && ms < MS_SENTINEL
      if (scheduled) {
        const secs = ms - Date.now() / 1000
        if (secs < 0) return 'late'
        if (secs <= SOON_SECS) return 'attention'
      }
      // needs work before it can roll → amber
      if (maint === 'PM' || maint === 'PENDING_DVIR') return 'attention'
      if (wash === 'DIRTY') return 'attention'
      // ready to go (same definition as the Available chip) → green
      const washReady = this.washedEnabled ? 'READY' : 'CLEANED'
      if (wash === washReady && maint === 'READY') return 'ready'
      return 'none'
    },
    // b-table per-row class; only problems get a colored left edge so the
    // desktop table is not striped end to end
    rowClass(item) {
      if (!item) return ''
      const state = this.rowState(item)
      return state === 'late' || state === 'attention' ? 'vb-row-' + state : ''
    },

    cellKey(item, field) {
      return item.name + '::' + field
    },
    // tap the chip to open the colored status options as a floating popover
    // anchored to it; tapping one sets it in a single step. the popover is
    // position: fixed and rendered once at the root, so it escapes the
    // table's overflow scrollport (no clipping) and opening it never reflows
    // the neighboring chip. tapping the same chip again toggles it closed.
    startEdit(item, field, ev) {
      const key = this.cellKey(item, field)
      if (this.editing_cell === key) { this.cancelEdit(true); return }
      // anchor to the colored chip itself (not the whole button, which on a
      // card also spans the "Wash"/"Maint" tag) so the menu lines up over it
      const trigger = ev && (ev.currentTarget || ev.target)
      const chip = trigger && trigger.querySelector ? trigger.querySelector('.vb-status-chip') : null
      const anchor = chip || trigger
      this._trigger_rect = anchor && anchor.getBoundingClientRect ? anchor.getBoundingClientRect() : null
      this.picker_item = item
      this.picker_field = field
      this.editing_cell = key
      // render off-screen first so the unpositioned popover never flashes
      this.picker_style = { top: '-9999px', left: '-9999px' }
      this.$nextTick(() => {
        this.positionPicker()
        document.addEventListener('mousedown', this.onDocClick, true)
        // a fixed popover anchored to a scrolled chip would detach, so any
        // scroll (window or the table's inner scrollport) closes it
        window.addEventListener('scroll', this.onWindowScroll, true)
        window.addEventListener('resize', this.onWindowResize, true)
        // land keyboard focus on the current option
        const pop = this.$refs.pickerPop
        const el = pop && (pop.querySelector('.vb-status-opt--current') || pop.querySelector('.vb-status-opt'))
        if (el && el.focus) el.focus()
      })
    },
    // center the menu vertically on the trigger so the middle option lands
    // directly over the button (native-select feel), and line the option
    // chips up horizontally over the trigger chip; clamp to the viewport
    positionPicker() {
      const rect = this._trigger_rect
      const pop = this.$refs.pickerPop
      if (!rect || !pop) return
      const margin = 8
      const pw = pop.offsetWidth
      const ph = pop.offsetHeight
      let top = rect.top + rect.height / 2 - ph / 2
      if (top + ph > window.innerHeight - margin) top = window.innerHeight - margin - ph
      if (top < margin) top = margin
      // undo the menu's internal left padding so an option chip sits exactly
      // over the trigger chip
      const firstChip = pop.querySelector('.vb-status-chip')
      const chipOffset = firstChip ? firstChip.getBoundingClientRect().left - pop.getBoundingClientRect().left : 0
      let left = rect.left - chipOffset
      if (left + pw > window.innerWidth - margin) left = window.innerWidth - margin - pw
      if (left < margin) left = margin
      this.picker_style = { top: Math.round(top) + 'px', left: Math.round(left) + 'px' }
    },
    // restoreFocus returns focus to the chip that opened the picker so
    // keyboard/AT users keep their place; mouse outside-click closes pass
    // false and leave focus alone
    cancelEdit(restoreFocus = false) {
      const key = this.editing_cell
      this.editing_cell = null
      this.picker_item = null
      this.picker_field = null
      this.picker_style = null
      document.removeEventListener('mousedown', this.onDocClick, true)
      window.removeEventListener('scroll', this.onWindowScroll, true)
      window.removeEventListener('resize', this.onWindowResize, true)
      if (restoreFocus && key) {
        this.$nextTick(() => {
          const el = this.$el.querySelector('[data-status-trigger="' + key + '"]')
          if (el && el.focus) el.focus()
        })
      }
    },
    // close when the next pointer-down lands outside the popover and outside
    // any chip trigger (so tapping another row's chip just re-targets it)
    onDocClick(e) {
      if (e.target.closest && (e.target.closest('.vb-picker-pop') || e.target.closest('.vb-chip-btn'))) return
      this.cancelEdit()
    },
    onWindowScroll() { this.cancelEdit() },
    onWindowResize() { this.cancelEdit() },
    // one tap sets + saves: optimistic update, patch from the response,
    // revert + toast on failure
    async setStatus(item, field, value) {
      this.cancelEdit(true)
      if (!value || value === item[field]) return
      const previous = item[field]
      this.$set(item, field, value)
      try {
        const body = { name: item.name }
        body[field] = value
        const update = this.vsmSource === 'tbn' ? tbn_update_vehicle_status : update_vehicle_status
        const { value: { data } = {} } = await update(this.tenant, body)
        // patch the row from the response instead of refetching, so open
        // history panels and scroll position survive the save
        if (data && typeof data === 'object') this.patchRow(item, data)
      } catch (e) {
        this.$set(item, field, previous)
        this.toast('The status update did not save. Try again.')
      }
    },
    // merge the updated record the api returns into the row, the same way
    // the list fetch merges each vehicle_info record into its row
    patchRow(item, record) {
      Object.keys(record).forEach(key => this.$set(item, key, record[key]))
      this.$set(item, 'vehicle_info', record)
    },

    noteFocus(item) {
      this.notes_original = item.notes
      // pauses the auto-refresh merge from overwriting this cell mid-edit
      this.notes_editing = item.name
    },
    async saveNotes(item) {
      // editing ends on blur, so clear the auto-refresh guard right away
      this.notes_editing = ''
      // capture before any await so a focus change cannot swap originals
      const original = this.notes_original
      if (item.notes === original) return
      try {
        const update = this.vsmSource === 'tbn' ? tbn_update_vehicle_status : update_vehicle_status
        await update(this.tenant, { name: item.name, notes: item.notes })
        this.flashNotesSaved(item.name)
      } catch (e) {
        // revert so the cell never keeps a value the server did not take
        this.$set(item, 'notes', original)
        this.toast('The notes did not save. Try again.')
      }
    },
    flashNotesSaved(name) {
      if (this.notes_saved_timer) clearTimeout(this.notes_saved_timer)
      this.notes_saved = name
      this.notes_saved_timer = setTimeout(() => { this.notes_saved = '' }, 1600)
    },

    toast(message) {
      try {
        this.$bvToast.toast(message, { title: 'VSM Preview', variant: 'warning', solid: true, autoHideDelay: 4000 })
      } catch (_) { /* toast plugin may not be ready on first paint */ }
    },

    toggleHistory(row) {
      const name = row.item.name
      if (this.expanded.includes(name)) {
        this.expanded = this.expanded.filter(n => n !== name)
        row.toggleDetails()
        return
      }
      this.expanded = [...this.expanded, name]
      row.toggleDetails()
      this.fetchHistory(name)
    },
    async fetchHistory(name) {
      const target = this.items_full.filter(item => item.name === name)[0]
      if (target && target.history_error) {
        // a retry goes back to the loading note while the fetch runs
        target.history_error = false
        this.$forceUpdate()
      }
      try {
        const { value: { data = [] } = {} } = await get_vehicle_info_history(this.tenant, name)
        if (target) {
          // history is a late-attached key, so force the re-render the
          // same way the legacy page does
          target.history = data
          this.$forceUpdate()
        }
      } catch (e) {
        // failure renders the per-row error note with its retry link
        if (target) {
          target.history_error = true
          this.$forceUpdate()
        }
      }
    },

    isLate(value) {
      return !!value && String(value).toLowerCase().indexOf('late') === 0
    },
    // time-only urgency for the departs-in value: red once overdue, amber
    // inside the soon window, default ink otherwise. kept separate from
    // rowState so a dirty bus that does not leave for days never paints its
    // countdown amber.
    departUrgency(item) {
      if (this.isLate(item.next_mvmt_delta)) return 'late'
      const ms = item.next_mvmt_ms
      if (typeof ms === 'number' && ms < MS_SENTINEL) {
        const secs = ms - Date.now() / 1000
        if (secs < 0) return 'late'
        if (secs <= SOON_SECS) return 'soon'
      }
      return ''
    },
    // the formatted next-depart stamp for the card head, '' when there is no
    // scheduled movement so the sub-line simply doesn't render
    departStamp(item) {
      const s = this.formatStamp(item.next_mvmt_time)
      return s === '-' ? '' : s
    },
    // day-plus-out departures paint quiet (normal weight + gray); only a
    // same-day countdown, or the late/soon states, earn the bold dark ink so
    // a wall of distant times stops out-shouting the vehicle names. prefers the
    // absolute stamp; falls back to a day token in the delta string for feeds
    // that carry no next_mvmt_ms.
    departIsDistant(item) {
      const u = this.departUrgency(item)
      if (u === 'late' || u === 'soon') return false
      const ms = item.next_mvmt_ms
      if (typeof ms === 'number' && ms < MS_SENTINEL) {
        return ms - Date.now() / 1000 >= 86400
      }
      return /\dd/.test(item.next_mvmt_delta || '')
    },

    // compact stamp by default ("6/11 7:59a"), with a tenant-configured
    // display format still winning. the api blends 'MM/DD hh:mm A' in as
    // its own fallback, so that exact value means "not configured". the
    // two-step parse mirrors the legacy formatDateTimeCustom filter,
    // including the non-iso input shape some rows carry.
    //
    // render in the tenant's timezone so a dispatcher in another zone still
    // reads the yard's wall-clock. moment.tz parses a zoneless value as
    // tenant-local (no shift) and converts an absolute (Z/offset) value into
    // tenant time. this matches the legacy boards' moment.tz.setDefault(tz)
    // without the global side effect that leaks into every other page. the
    // compact case formats off the zoned moment directly, since routing
    // through formatDateTimeCompact would re-parse and re-localize to the
    // viewer, undoing the conversion.
    formatStamp(value) {
      if (!value || typeof value !== 'string') return '-'
      const tz = (this.summary && this.summary.timezone) || ''
      const tenantFormat = (this.summary && this.summary.datetime_format) || ''
      const isIso = value.indexOf('T') !== -1
      const m = tz
        ? (isIso ? moment.tz(String(value), tz) : moment.tz(String(value), ['MM/DD hh:mm A'], tz))
        : (isIso ? moment(String(value)) : moment(String(value), ['MM/DD hh:mm A']))
      if (!m.isValid()) return '-'
      if (tenantFormat && tenantFormat !== 'MM/DD hh:mm A') return m.format(tenantFormat)
      // same compact shape as formatDateTimeCompact ("6/11 7:59a"), kept on
      // the zoned moment to preserve the tenant timezone
      return m.format('M/D h:mma').replace(/(a|p)m$/, '$1') || '-'
    },

    // the primary movement carries a driver array (co-drivers), while each
    // upcoming booking in next_mvmt_list carries a single driver string
    formatDrivers(value) {
      if (Array.isArray(value)) return value.length ? value.join(', ') : '-'
      return value || '-'
    },

    // departs-in sorts on the precomputed millisecond delta from the api,
    // the display string is not sortable text (mirrors the legacy page)
    tableSortCompare(itemA, itemB, key) {
      if (key !== 'next_mvmt_delta') return false
      return itemA['next_mvmt_ms'] - itemB['next_mvmt_ms']
    },
  },
}
</script>

<style scoped>
.vb-root {
  /* tight 10px side gutters to match the support-ticket list spacing
     (.stl-root padding:0 + .stl-table-inner margin:0 10px); vertical kept */
  padding: 12px 10px 32px 10px;
  text-align: left;
  font-size: 14px;
  color: #1d1d1f;
}
@media (max-width: 720px) {
  .vb-root { padding: 12px 10px 24px 10px; }
}

/* header rail, matching the ops board / erm rail language */
.vb-rail {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  /* square rail edge, soft right side (support-ticket rail treatment) */
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 8px;
}
.vb-rail-text {
  display: flex; flex-direction: column; justify-content: center;
  min-height: 32px;
  /* grow to push the controls to the right edge on one line; the min-width
     is what makes the control cluster wrap to its own row (rather than the
     title squeezing) once the header gets tight. content-driven, so it holds
     at any width and survives the wider title the tenant-context chip adds,
     where a fixed breakpoint would leave an awkward band. */
  flex: 1 1 auto;
  min-width: 240px;
}
.vb-rail-eyebrow {
  font-size: 11px; line-height: 1.4;
  text-transform: uppercase; letter-spacing: 0.6px;
  color: var(--fi-ops-eyebrow); font-weight: 600;
  margin-bottom: 1px;
}
.vb-rail-title {
  margin: 0; padding: 0;
  font-size: 19px; line-height: 1.2;
  font-weight: 700; color: #1d1d1f;
  letter-spacing: -0.1px;
  display: flex; align-items: center; gap: 8px;
}
/* admin tenant-context chip: names whose data the page renders when the
   route tenant differs from the navbar selection. same treatment as the
   erm rail (.od-rail-tenant-chip). */
.vb-rail-tenant-chip {
  display: inline-flex; align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #c8d4df;
  color: var(--fi-ops-eyebrow);
  font-size: 11px; font-weight: 600;
  letter-spacing: 0;
  white-space: nowrap;
}
/* right cluster: the control buttons with the freshness stamp tucked beneath
   them, so "updated" reads next to the refresh/auto controls it belongs with.
   no margin-left:auto — rail-text's grow pushes this to the right edge on one
   line, and it left-aligns cleanly when it wraps to its own row. */
.vb-rail-actions-col {
  display: flex; flex-direction: column; align-items: flex-end; gap: 4px;
  flex: 0 0 auto;
}
.vb-rail-actions {
  display: inline-flex; align-items: center; gap: 6px;
  flex-shrink: 0;
}
.vb-base-select { width: 170px; }

@media (max-width: 600px) {
  /* the control cluster no longer fits beside the title at phone width, so
     let the title take the first row and the controls wrap to their own row
     under it (the base rail is flex-wrap: wrap) */
  .vb-rail { padding: 10px 12px; gap: 8px; }
  .vb-rail-text { flex: 1 1 100%; min-width: 0; }
  .vb-rail-title { font-size: 17px; }
  /* full-width control row, left-aligned, buttons allowed to wrap */
  .vb-rail-actions-col { align-items: flex-start; width: 100%; }
  .vb-rail-actions { flex-wrap: wrap; }
  /* the freshness stamp is secondary; drop it on a tight header */
  .vb-rail-updated { display: none; }
  /* compact base select so the control row fits one line */
  .vb-base-select { width: 108px; }
}

/* inline load error, same look as the ops board banner */
.vb-error {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; flex-wrap: wrap;
  background: #fff8ef;
  border: 1px solid #f0d9b5;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #7a5400;
}

/* filter chips */
.vb-chip-filters {
  display: flex; flex-wrap: wrap; gap: 14px;
  align-items: center;
  margin-bottom: 12px;
}
/* the pill cluster (available + both groups); inline-wraps on desktop, one
   swipeable row on mobile */
.vb-chip-scroll {
  display: flex; flex-wrap: wrap; gap: 14px;
  align-items: center;
  /* size to content (do not grow) so the tags trigger packs in right after the
     maint group; .vb-search keeps margin-left:auto to hold the right edge */
  flex: 0 1 auto; min-width: 0;
}
.vb-chip-group {
  display: flex; flex-wrap: wrap; gap: 6px;
  align-items: center;
}
.vb-chip-group-label {
  font-size: 10.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.4px;
  color: #6b7280;
}
.vb-chip {
  position: relative;
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 999px;
  padding: 4px 12px; font-size: 12.5px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .12s, border-color .12s, color .12s, filter .12s;
  white-space: nowrap;
}
/* invisible hit-area extension to ~38px; the painted pill stays the same */
.vb-chip::before {
  content: '';
  position: absolute;
  top: -6px; bottom: -6px; left: 0; right: 0;
}
.vb-chip:not([class*="vb-fc-"]):hover { background: #eef4fb; border-color: #b8c8da; }
.vb-chip--active {
  background: var(--fi-ops-accent); border-color: var(--fi-ops-accent); color: #fff;
}
.vb-chip--active:hover { background: var(--fi-ops-accent-strong); border-color: var(--fi-ops-accent-strong); }

/* filter pills echo the row status-chip palette: a subtle tint when idle,
   the chip's own ink as a solid fill when active. colors mirror
   WASH_CHIP_CLASSES / MAINT_CHIP_CLASSES (.vb-chip--green etc.). */
/* idle pills carry their status color clearly — a medium tint of the bold
   hue plus the full bold-hue border, so the row reads as the new palette;
   selected pills fill solid (the exact chip color, below) */
.vb-fc-green  { background: #fff; border-color: #1fab42; color: #146230; }
.vb-fc-cyan   { background: #fff; border-color: #1099bf; color: #095f78; }
.vb-fc-teal   { background: #fff; border-color: #1f9a7c; color: #105748; }
.vb-fc-blue   { background: #fff; border-color: #1668d6; color: #11468f; }
.vb-fc-brown  { background: #fff; border-color: #a85e22; color: #76411a; }
.vb-fc-purple { background: #fff; border-color: #8a3fb5; color: #5f2780; }
.vb-fc-amber  { background: #fff; border-color: #d39410; color: #7e5407; }
.vb-fc-red    { background: #fff; border-color: #d62f2f; color: #b22525; }
.vb-fc-gray   { background: #fff; border-color: #6b7280; color: #4b5563; }
.vb-chip[class*="vb-fc-"] { border-width: 1.5px; }
/* idle outline pills get a neutral wash on hover; brightness() would dim the
   white fill to near-invisible. scoped to idle so the active solid fill keeps
   its own hover. */
.vb-chip[class*="vb-fc-"]:not(.vb-chip--active):hover { background: #f4f7fa; }
.vb-fc-green.vb-chip--active  { background: #1fab42; border-color: #1fab42; color: #15202b; }
.vb-fc-cyan.vb-chip--active   { background: #1099bf; border-color: #1099bf; color: #15202b; }
.vb-fc-teal.vb-chip--active   { background: #1f9a7c; border-color: #1f9a7c; color: #15202b; }
.vb-fc-amber.vb-chip--active  { background: #d39410; border-color: #d39410; color: #15202b; }
.vb-fc-blue.vb-chip--active   { background: #1668d6; border-color: #1668d6; color: #fff; }
.vb-fc-brown.vb-chip--active  { background: #a85e22; border-color: #a85e22; color: #fff; }
.vb-fc-purple.vb-chip--active { background: #8a3fb5; border-color: #8a3fb5; color: #fff; }
.vb-fc-red.vb-chip--active    { background: #d62f2f; border-color: #d62f2f; color: #fff; }
.vb-fc-gray.vb-chip--active   { background: #5f6772; border-color: #5f6772; color: #fff; }

/* tags: compact menu trigger sitting in the filter row next to the chip
   groups; outside .vb-chip-scroll so its menu isn't clipped by that row's
   overflow. */
.vb-tag-dd { flex: 0 0 auto; }
.vb-tag-dd >>> .dropdown-toggle {
  border-radius: 999px; font-size: 12.5px; font-weight: 600;
  padding: 4px 12px;
}
.vb-tag-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 16px; height: 16px; margin-left: 6px; padding: 0 4px;
  border-radius: 999px; background: var(--fi-ops-accent); color: #fff;
  font-size: 10.5px; line-height: 1;
}
.vb-tag-opt {
  display: flex; align-items: center; gap: 8px;
  margin: 0; padding: 3px 2px; font-size: 13px; cursor: pointer;
  white-space: nowrap;
}
.vb-tag-opt-count { margin-left: auto; color: #6b7280; font-size: 11.5px; }
.vb-tag-form { max-height: 320px; overflow-y: auto; }

/* find-vehicle/driver search, right-aligned in the filter row */
.vb-search {
  margin-left: auto;
  position: relative;
  display: inline-flex; align-items: center;
  flex: 0 1 240px;
}
.vb-search-icon {
  position: absolute; left: 11px;
  color: #9aa3ad; font-size: 12px; pointer-events: none;
}
.vb-search-input {
  width: 100%;
  padding: 5px 26px 5px 28px;
  font-size: 13px;
  border: 1px solid #d6dde6; border-radius: 999px;
  background: #fff; color: #1d1d1f;
  transition: border-color .12s, box-shadow .12s;
}
.vb-search-input::placeholder { color: #9aa3ad; }
.vb-search-input:focus {
  outline: none;
  border-color: var(--fi-ops-accent);
  box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.2);
}
.vb-search-clear {
  position: absolute; right: 6px;
  background: none; border: none; padding: 2px 5px;
  color: #9aa3ad; cursor: pointer; font-size: 12px; line-height: 1;
}
/* invisible hit-area extension to ~32px, matching the .vb-chip-btn convention */
.vb-search-clear::before {
  content: '';
  position: absolute;
  top: -9px; bottom: -9px; left: -7px; right: -7px;
}
.vb-search-clear:hover { color: #4b5563; }
@media (max-width: 640px) {
  /* the filter block pins to the top of the scroll so search + pills stay
     reachable down a long card list. the navbar is in normal flow (not
     fixed), so it scrolls off and top:0 lands at the viewport edge. the
     negative side margins bleed the white backing to the screen edges so
     cards do not show through the gutters when stuck. */
  .vb-chip-filters {
    position: sticky; top: 0; z-index: 20;
    flex-direction: column; align-items: stretch; gap: 8px;
    background: #fff;
    margin: 0 -16px 10px -16px;
    padding: 8px 16px;
    border-bottom: 1px solid #eef0f3;
  }
  /* search sits below the status pills, full width, 16px to dodge iOS
     focus-zoom (order:1 puts it after the default-order chips + tags) */
  .vb-search { order: 1; margin-left: 0; flex: 0 0 auto; width: 100%; }
  .vb-search-input { font-size: 16px; }
  /* one swipeable pill row instead of three stacked groups. the scrollbar
     chrome read as janky, so it's hidden; a soft right-edge fade signals
     there's more to swipe without a visible track. */
  .vb-chip-scroll {
    flex-wrap: nowrap; overflow-x: auto; gap: 10px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    /* as an overflow-x scroll container this flex child won't honor the
       column parent's stretch, so pin the width and bleed past the bar's
       right padding to the screen edge — that puts the fade where it's
       actually visible instead of in the clipped-off region. */
    width: calc(100% + 16px);
    margin-right: -16px;
    -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 22px), transparent);
    mask-image: linear-gradient(to right, #000 calc(100% - 22px), transparent);
  }
  .vb-chip-scroll::-webkit-scrollbar { display: none; }
  /* trailing scroll space (a flex spacer, more reliable than end padding on
     iOS Safari) so the last pill can swipe clear of the right-edge fade and
     the -16px bleed instead of staying stuck under them. without it a wide
     last chip like "Unsafe DVIR" can never be read at the end of the row.
     sized past the 22px mask so the final chip lands fully in the clear. */
  .vb-chip-scroll::after {
    content: '';
    flex: 0 0 28px;
  }
  /* pin the group wrappers too — the rule below only pins the chips, so the
     groups kept flex-shrink:1 and collapsed under the no-wrap row, letting
     their chips spill out and overlap. */
  .vb-chip-group { flex-wrap: nowrap; flex: 0 0 auto; }
  /* keep the tags trigger from stretching full-width in the mobile column */
  .vb-tag-dd { align-self: flex-start; }
  .vb-chip-scroll > .vb-chip,
  .vb-chip-group > .vb-chip { flex: 0 0 auto; }
}

/* table card */
.vb-card {
  background: #fff;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 0;
  /* overflow visible so the status picker (a root-level position:fixed popover)
     and chip hover rings are never clipped by the card. the table's horizontal
     scroll lives on the inner .table-responsive wrapper, not here. */
  overflow: visible;
}

/* quiet fact card for tenants without a telematics feed */
.vb-no-telematics {
  background: #fff;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 44px 24px;
  text-align: center;
  color: #6b7280;
}
.vb-no-telematics .fa {
  font-size: 26px;
  color: #b9c2cc;
  margin-bottom: 10px;
}
.vb-no-telematics-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 4px;
}
.vb-no-telematics-sub {
  font-size: 13px;
  max-width: 52ch;
  margin: 0 auto;
  line-height: 1.5;
}
.vb-card >>> .table.b-table { margin-bottom: 0; }

/* keep bootstrap-vue's responsive wrapper as the horizontal scroll container at
   all widths so a fleet with more columns than the viewport is wide stays
   swipeable instead of bleeding off the right edge and getting clipped (this is
   how the Support Ticket list behaves). the status picker is a root-level
   position:fixed popover, so this overflow never clips it. overflow-y is pinned
   to hidden rather than left to compute to auto from overflow-x:auto, so classic
   (non-overlay) scrollbars in Safari/Firefox can't spawn a phantom vertical
   scrollbar; the wrapper is content-height (no max-height) so a tall fleet is
   never clipped and the page scrolls to reveal every row. trade-off: the thead
   anchors to this scroll container rather than page-pinning, same as the Support
   Ticket list's header. */
.vb-card >>> .table-responsive { overflow-x: auto; overflow-y: hidden; }

/* the canonical dense table pattern; deep selectors because bootstrap-vue
   renders the inner table */
.vb-root >>> .table.b-table { font-size: 13px; border-collapse: collapse; }
.vb-root >>> .table.b-table th,
.vb-root >>> .table.b-table td {
  border-left: none !important; border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.vb-root >>> .table.b-table th:first-child,
.vb-root >>> .table.b-table td:first-child { padding-left: 12px !important; }
.vb-root >>> .table.b-table th:last-child,
.vb-root >>> .table.b-table td:last-child   { padding-right: 12px !important; }
.vb-root >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  position: sticky; top: 0; z-index: 2;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
.vb-root >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; }
/* problem rows get a left edge that matches the card rails (late = red,
   attention = amber); ready/idle rows stay clean so the eye finds problems */
.vb-root >>> .table.b-table tr.vb-row-late td:first-child { box-shadow: inset 3px 0 0 0 #d64545; }
.vb-root >>> .table.b-table tr.vb-row-attention td:first-child { box-shadow: inset 3px 0 0 0 #e0a32e; }

.vb-root >>> th[aria-sort] { background-image: none !important; padding-right: 0.75rem !important; white-space: nowrap; }
.vb-root >>> th[aria-sort]::before { display: none !important; }
.vb-root >>> th[aria-sort]::after  { display: inline-block !important; margin-left: 4px; font-size: 10px; color: #999; vertical-align: middle; float: none !important; width: auto !important; height: auto !important; opacity: 1 !important; }
.vb-root >>> th[aria-sort="none"]::after       { content: "\2195" !important; }
.vb-root >>> th[aria-sort="ascending"]::after  { content: "\25B2" !important; color: var(--fi-rail-accent); }
.vb-root >>> th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent); }

/* busy / empty states */
.vb-busy {
  padding: 24px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}
.vb-empty {
  padding: 14px 12px;
  font-size: 13px;
  color: #6b7280;
}
.vb-clear-btn {
  background: none; border: none; padding: 0; margin-left: 6px;
  font-size: 13px; color: var(--fi-ops-accent-strong); text-decoration: underline;
  cursor: pointer;
}
.vb-clear-btn:hover { color: var(--fi-ops-accent-deep); }

/* expand (history) button */
.vb-expand-btn {
  position: relative;
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  background: #fff; border: 1px solid #d6dde6; border-radius: 6px;
  color: #4b5563; cursor: pointer; padding: 0;
}
/* invisible hit-area extension to ~40px around the 28px button */
.vb-expand-btn::before {
  content: '';
  position: absolute;
  top: -6px; bottom: -6px; left: -6px; right: -6px;
}
.vb-expand-btn:hover { background: #eef4fb; border-color: #b8c8da; }
.vb-expand-btn .fa { font-size: 11px; transition: transform .15s ease; }
.vb-expand-btn--open .fa { transform: rotate(90deg); }

/* vehicle name link */
.vb-vehicle-link { font-weight: 600; white-space: nowrap; }

/* status chips: a quiet two-tier tag. set statuses render as a soft tinted
   tag (hue text on a light wash + hairline); the alarm states (out, oos,
   unsafe) keep a solid fill so they stay loud in a field of quiet tags; unset
   is a ghost. shape stays the support-ticket priority-tag rectangle so the
   wash/maint chips still column up regardless of label. */
.vb-status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  /* fixed width so wash/maint buttons line up regardless of label; the rare
     system-set DVIR labels can still grow past this */
  min-width: 88px;
  padding: 3px 8px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid transparent;
  color: #fff;
  white-space: nowrap;
}
/* set statuses: soft tinted tags. hue text on a light wash with a matching
   hairline — quiet enough that a wall of them never out-shouts the departure
   columns, but the hue still encodes status at a glance. text colors verified
   AA (>=4.5:1) on their tint. */
.vb-chip--green  { background: #e8f6ec; border-color: #bfe3cb; color: #14792f; }  /* READY */
.vb-chip--cyan   { background: #e3f3f9; border-color: #b2deec; color: #0a6c88; }  /* CLEANED */
.vb-chip--teal   { background: #e6f4ef; border-color: #b6e0d3; color: #15705a; }  /* WASHED */
.vb-chip--amber  { background: #fbf0d6; border-color: #f0dba4; color: #8a5a06; }  /* PENDING_DVIR */
.vb-chip--blue   { background: #e7f0fc; border-color: #bdd5f3; color: #1255b0; }  /* PM */
.vb-chip--brown  { background: #f5ece2; border-color: #e6d1bc; color: #884b1a; }  /* DIRTY */
/* alarm states stay loud: a fully filled chip is reserved for the statuses
   that warrant an alarm in a field of quiet tags */
.vb-chip--purple { background: #8a3fb5; border-color: #8a3fb5; color: #fff; }  /* OUT */
.vb-chip--red    { background: #d62f2f; border-color: #d62f2f; color: #fff; }  /* OOS / UNSAFE */
/* unset / unknown: a ghost — no fill, faint hairline, muted dash. the most
   common cell on a real fleet, so it stays the quietest thing on the row */
.vb-chip--gray   { background: #f7f9fb; border-color: #dbe1e8; color: #6b7280; }  /* unset */
/* caret tucked inside the trigger chip so wash/maint read as dropdown
   buttons; read-only chips (history, non-editable role) omit it */
.vb-status-caret { margin-left: 6px; font-size: 10px; opacity: 0.9; }
/* the trigger pushes its caret to the right edge of the fixed-width chip
   (dropdown-button look); the picker options pin to the same width so the
   menu lands square over the button; history chips stay content-sized */
.vb-status-chip--trigger { justify-content: space-between; }
.vb-picker-pop .vb-status-opt .vb-status-chip { width: 88px; }
.vb-hist-what .vb-status-chip { min-width: 0; }

/* clickable chip wrapper for the editable cells */
.vb-chip-btn {
  position: relative;
  background: none; border: none; padding: 0;
  display: inline-flex; align-items: center;
  cursor: pointer;
  border-radius: 5px;
}
/* invisible hit-area extension to ~36px over the short chip */
.vb-chip-btn::before {
  content: '';
  position: absolute;
  top: -9px; bottom: -9px; left: -4px; right: -4px;
}
.vb-chip-btn:hover .vb-status-chip { filter: brightness(1.1); box-shadow: 0 0 0 2px rgba(70, 130, 180, 0.25); }
/* the trigger whose picker is open reads as held: ringed chip + flipped caret */
.vb-chip-btn--active .vb-status-chip { box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.55); }
.vb-chip-btn--active .vb-status-caret { transform: rotate(180deg); }

/* anchored status picker: a single floating popover (position: fixed, placed
   in JS from the trigger's rect) shared by the whole board. opening it never
   moves the neighboring chip, and fixed positioning escapes the table's
   overflow scrollport so it is never clipped. */
.vb-picker-pop {
  position: fixed;
  z-index: 1080;
  display: flex; flex-direction: column; align-items: stretch; gap: 2px;
  min-width: 150px; max-width: 240px;
  background: #fff;
  border: 1px solid #d6dde6;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(20, 40, 60, 0.16);
  padding: 5px;
}
/* one full-width menu row per option: the solid status chip on the left, a
   check on the current one. comfortable row height so a mis-tap onto an
   adjacent destructive status is hard */
.vb-status-opt {
  background: none; border: none; margin: 0;
  cursor: pointer; border-radius: 6px; line-height: 1;
  width: 100%; min-height: 40px; padding: 4px 8px;
  display: inline-flex; align-items: center; justify-content: space-between; gap: 10px;
}
.vb-status-opt:hover { background: #f2f5f8; }
.vb-status-opt--current { background: #eef4fb; }
.vb-status-opt-check { color: var(--fi-ops-accent); font-size: 12px; }
.vb-status-opt:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: -2px; }
@media (max-width: 640px) {
  /* roomier menu + 44px rows on phones */
  .vb-picker-pop { min-width: 180px; max-width: calc(100vw - 24px); padding: 6px; }
  .vb-status-opt { min-height: 44px; }
  .vb-status-opt .vb-status-chip { font-size: 12px; }
}

/* family focus standard */
.vb-chip:focus-visible,
.vb-chip-btn:focus-visible,
.vb-expand-btn:focus-visible,
.vb-clear-btn:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}

/* notes */
.vb-notes-input { min-width: 180px; transition: border-color .25s, background-color .25s; }
/* brief confirmation after a successful blur-save; a timer clears it */
.vb-notes-input--saved { border-color: #b5e3c5; background-color: #e7f8ed; }

/* departs-in urgency; on-time keeps default ink, soon is amber, late is red */
.vb-late { color: #b91c1c; font-weight: 600; }
.vb-soon { color: #b45309; font-weight: 600; }
.vb-stack-line { display: block; }

/* status history detail panel */
.vb-history { padding: 4px 8px 8px 40px; }
.vb-history-table { font-size: 12px; border-collapse: collapse; }
.vb-history-table th {
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
  color: #6b7280; text-align: left;
  padding: 2px 14px 2px 0;
  border-bottom: 1px solid #e0e0e0;
}
.vb-history-table td {
  padding: 3px 14px 3px 0;
  border-bottom: 1px solid #eef0f3;
  color: #374151;
}
.vb-history-empty { font-size: 12px; color: #6b7280; font-style: italic; }
.vb-history-error { font-size: 12px; color: #6b7280; }
.vb-history-error .vb-clear-btn { font-size: 12px; }

/* mobile card layout (max-width: 640px branch) */
/* bottom room so the last card clears the floating "Ask Fleet Assistant"
   launcher (fixed, bottom-right) instead of tucking under it */
.vb-card-list { display: flex; flex-direction: column; gap: 10px; padding-bottom: 84px; }
.vb-vcard {
  position: relative;
  background: #fff;
  border: 1px solid #e0e6ec;
  border-left: 5px solid #e0e6ec;
  /* square the railed (left) edge so the colored bar reads as a rectangle,
     not a rounded lozenge — mirrors the support-ticket admin-rail trick
     (border-radius 0 on the railed side) */
  border-radius: 0 10px 10px 0;
  /* equal top/bottom breathing room: the name's line-height leading adds ~2px
     over the top padding, so the bottom is a hair larger to match visually */
  padding: 10px 14px 11px 14px;
}
/* readiness rail: red = late / out of service, amber = soon / needs work,
   green = ready, neutral otherwise. differentiates the cards at a glance and
   makes a late or out-of-service bus pop. */
.vb-vcard--late { border-left-color: #d64545; }
.vb-vcard--attention { border-left-color: #e0a32e; }
.vb-vcard--ready { border-left-color: #46a36b; }
.vb-vcard--none { border-left-color: #e0e6ec; }
/* tier-1 lead: the vehicle name is the identity you scan for, so it leads by
   size + weight (15px/600 -> 17px/700). keeps the shipped link-blue. */
.vb-vcard .vb-vehicle-link { font-size: 17px; font-weight: 700; }
.vb-vcard-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
/* depart label + countdown sit inline as one strong lead line beside the name;
   the precise stamp drops to a quiet sub-line right-aligned beneath it */
.vb-vcard-depart { display: flex; flex-direction: column; align-items: flex-end; gap: 1px; text-align: right; flex-shrink: 0; }
.vb-vcard-depart-main { display: flex; flex-direction: row; align-items: baseline; gap: 6px; }
.vb-vcard-depart-label {
  font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.4px; color: #6b7280;
}
.vb-vcard-depart-val { font-size: 16px; font-weight: 600; color: #1d1d1f; line-height: 1.2; }
/* a day-plus out reads quiet: normal weight, medium gray. only same-day
   countdowns (and the late/soon states below) keep the bold dark ink. */
.vb-vcard-depart-val--distant { font-weight: 400; color: #6b7280; }
/* precise next-depart stamp under the countdown */
.vb-vcard-depart-when { font-size: 11px; color: #6b7280; line-height: 1.2; }
/* urgency wins over the depart-val default ink so the card time matches the
   list's late/almost-late coloring (same single-class specificity otherwise
   lets the later default rule win, leaving the card time stuck dark) */
.vb-vcard-depart-val.vb-late { color: #b91c1c; }
.vb-vcard-depart-val.vb-soon { color: #b45309; }
/* status chips + the expand toggle share one row: the chips grow and wrap on
   the left, Details pins to the right, so the card sheds its old standalone
   button band. a touch more air above separates this tier from the lead line. */
/* status row: wash + maint as two equal fixed cells with the expand toggle
   pinned right. equal grid columns keep the boxes in the same spot card-to-card
   (muscle memory + easy tapping while scrolling a long list on a phone). */
.vb-vcard-actionrow {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: stretch;
  gap: 10px;
  margin-top: 11px;
}
/* each status cell stacks a small caption over the chip and fills its whole
   grid column, so the fixed-width box itself is the tap target */
.vb-vcard-statusctl {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 3px; min-height: 44px;
  padding: 0; background: none; border: none;
}
.vb-vcard-statusctl::before { display: none; }
.vb-vcard-chip-tag {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px;
  color: #6b7280; margin: 0;
}
/* re-enable the shared 88px chip width on cards so common labels align
   box-to-box; the rare DVIR labels grow within the fixed cell. the caret hugs
   the right edge (dropdown look) via the inherited --trigger space-between. */
.vb-vcard-statusctl .vb-status-chip {
  min-width: 88px; max-width: 100%;
  font-size: 11px; padding: 5px 10px;
}
.vb-vcard-statusctl .vb-status-caret { font-size: 9px; }
/* ghost placeholder for an unset editable status: no fill, dashed hairline,
   a muted "Set" that reads as "tap to set" rather than a real gray status */
.vb-vcard-statusctl .vb-status-chip--set {
  background: transparent; border-style: dashed;
  border-color: #c3ccd6; color: #8a93a0;
}
.vb-chip-btn:hover .vb-status-chip--set {
  border-color: var(--fi-ops-accent); color: var(--fi-ops-accent-strong);
}
.vb-vcard-more {
  /* right-aligned in its third and dropped to the bottom of the cell so the
     label rides the same line as the wash/maint chips (which sit below their
     captions), not the vertical middle of the row. a slim box keeps the label
     centered with its caret (align-items:center, not flex-end, which sank the
     caret below the text); a ::before restores the ~44px tap target. */
  position: relative;
  justify-self: end; align-self: end;
  padding: 6px 2px;
  background: none; border: none;
  font-size: 13px; color: var(--fi-ops-accent-strong); cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  white-space: nowrap;
}
/* invisible hit-area extension to ~44px around the slim label (mirrors the
   .vb-chip / .vb-search-clear convention) */
.vb-vcard-more::before {
  content: '';
  position: absolute;
  top: -9px; bottom: -9px; left: -10px; right: -10px;
}
.vb-vcard-more:hover { color: var(--fi-ops-accent-deep); }
.vb-vcard-detail { margin-top: 4px; padding-top: 10px; border-top: 1px solid #eef0f3; }
.vb-vcard-row { display: flex; justify-content: space-between; gap: 14px; font-size: 13px; padding: 3px 0; }
.vb-vcard-row-label { color: #6b7280; }
.vb-vcard-row-val { color: #374151; text-align: right; }
.vb-vcard-notes { align-items: center; }
.vb-vcard-notes .vb-notes-input { min-width: 0; width: 160px; }
/* status history as stacked entries; the desktop table would overflow the
   card on a phone (the clipped "Notes" column) */
.vb-vcard-history { padding: 8px 0 0 0; }
.vb-hist-entry { padding: 6px 0; border-bottom: 1px solid #f0f2f5; }
.vb-hist-entry:last-child { border-bottom: none; }
.vb-hist-top { display: flex; justify-content: space-between; gap: 10px; font-size: 12px; }
.vb-hist-when { color: #374151; font-weight: 600; white-space: nowrap; }
.vb-hist-by { color: #9aa3ad; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vb-hist-what { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 5px; }
.vb-hist-note { margin-top: 5px; font-size: 12px; color: #4b5563; }

/* keep iOS Safari from zooming the viewport on focus: any form control
   under 16px triggers the auto-zoom, which never reflows back cleanly */
@media (max-width: 640px) {
  .vb-card-list .vb-notes-input,
  .vb-card-list input { font-size: 16px; }
}

/* header: auto-refresh toggle + the last-updated note */
.vb-rail-updated { font-size: 11px; color: #9aa3ad; white-space: nowrap; line-height: 1.2; }

/* result count + tz caption, sitting directly above the list so the count
   reads beside the filtered rows it describes */
.vb-results-meta {
  display: flex; align-items: baseline; gap: 10px;
  margin: 2px 2px 6px 2px;
  font-size: 12px;
}
.vb-results-count { font-weight: 600; color: #4b5563; }
/* push the tz to the right edge so it sits over the time columns / the card's
   right-aligned departs block, near the times it labels */
.vb-results-tz { color: #6b7280; margin-left: auto; }
.vb-auto-btn { display: inline-flex; align-items: center; gap: 6px; }
.vb-auto-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #c2cad3; flex-shrink: 0;
}
/* a calm green pulse signals the board is live without shouting */
.vb-auto-dot--live { background: #46a36b; animation: vb-pulse 2.4s ease-out infinite; }
.vb-auto-btn--on { border-color: #bfe3cb; color: #14792f; }
@keyframes vb-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(70, 163, 107, 0.45); }
  70%  { box-shadow: 0 0 0 6px rgba(70, 163, 107, 0); }
  100% { box-shadow: 0 0 0 0 rgba(70, 163, 107, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .vb-auto-dot--live { animation: none; }
}

/* print-only header; hidden on screen, revealed by the print stylesheet */
.vb-print-meta { display: none; }

/* info modal: legend rows drawn with the board's own chips + triage colors,
   so the key always matches what is on screen */
.vb-info { font-size: 13.5px; color: #374151; }
.vb-info-sec { margin-bottom: 18px; }
.vb-info-sec:last-child { margin-bottom: 0; }
.vb-info-h {
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px;
  color: var(--fi-ops-eyebrow); margin: 0 0 8px 0;
}
.vb-info-p { margin: 0 0 6px 0; line-height: 1.45; }
.vb-info-p:last-child { margin-bottom: 0; }
.vb-info-legend, .vb-info-chips { list-style: none; margin: 0; padding: 0; }
.vb-info-legend li {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 4px 0; line-height: 1.4;
}
.vb-info-dot {
  width: 10px; height: 10px; border-radius: 3px;
  margin-top: 4px; flex-shrink: 0;
}
.vb-info-dot--late { background: #d64545; }
.vb-info-dot--attention { background: #e0a32e; }
.vb-info-dot--ready { background: #46a36b; }
.vb-info-chips li { display: flex; align-items: center; gap: 10px; padding: 4px 0; }
.vb-info-chips .vb-status-chip { flex-shrink: 0; }
.vb-info-desc { color: #4b5563; line-height: 1.4; }
</style>

<style>
/* print path for the vsm preview, active only while the board is mounted
   (body.vsm-board-print set in mounted/beforeDestroy). hides the app chrome
   and the screen-only controls, unclips the responsive table, reveals the
   self-documenting header, and forces the chip fills to print. mirrors the
   trip-sheet wall board. */
@media print {
  body.vsm-board-print #navbar,
  body.vsm-board-print .fa-launcher-root,
  body.vsm-board-print .vb-rail-actions-col,
  body.vsm-board-print .vb-chip-filters,
  body.vsm-board-print .vb-error,
  body.vsm-board-print .vb-picker-pop,
  body.vsm-board-print .vb-expand-btn,
  body.vsm-board-print .vb-status-caret {
    display: none !important;
  }
  body.vsm-board-print .vb-print-meta {
    display: flex !important;
    flex-direction: column;
    gap: 2px;
    margin: 0 0 10px 0;
  }
  body.vsm-board-print .vb-print-meta-title { font-size: 16px; font-weight: 700; }
  body.vsm-board-print .vb-print-meta-sub,
  body.vsm-board-print .vb-print-meta-when { font-size: 11px; color: #555; }
  /* print the whole roster rather than an inner scroll viewport */
  body.vsm-board-print .vb-card,
  body.vsm-board-print .table-responsive { overflow: visible !important; }
  /* keep the status-chip fills on paper */
  body.vsm-board-print .vb-root,
  body.vsm-board-print .vb-status-chip {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
